import QRCode from 'qrcode';

/**
 * The QR code a Saudi (ZATCA) simplified tax invoice must carry.
 *
 * The payload is a base64-encoded TLV sequence of five tags, in this order:
 *
 *   1  seller name
 *   2  seller VAT registration number
 *   3  invoice timestamp, ISO 8601
 *   4  invoice total INCLUDING VAT
 *   5  VAT amount
 *
 * Each entry is [tag byte][length byte][UTF-8 value]. The length is the byte
 * length of the value, NOT its character count — an Arabic seller name is two
 * bytes per letter, and getting that wrong is the classic reason a scanner
 * rejects an otherwise correct invoice.
 *
 * Rendering is synchronous on purpose: `buildReceiptHtml` is a pure, sync
 * string builder and must stay one. `QRCode.create()` gives us the module
 * matrix without any async work, and we draw the SVG ourselves — which also
 * means no canvas, no data-URL bloat and a crisp code at any print DPI.
 */

/**
 * Build the base64 TLV payload.
 *
 * Values go in VERBATIM — no trimming, no reformatting. This is byte-compared
 * against a QR the ERP produced for a real invoice (see buildA4Html.test.ts),
 * and the caller decides what a field should contain. Empty tags are still
 * emitted.
 */
export function buildZatcaPayload(input: {
  sellerName: string;
  vatNumber: string;
  /** ISO 8601 instant. */
  timestamp: string;
  /** Total including VAT. */
  total: string;
  /** VAT amount. */
  vat: string;
}): string {
  const chunks: number[] = [];
  const encoder = new TextEncoder();

  const push = (tag: number, value: string): void => {
    const bytes = encoder.encode(value ?? '');
    chunks.push(tag, bytes.length, ...bytes);
  };

  push(1, input.sellerName);
  push(2, input.vatNumber);
  push(3, input.timestamp);
  push(4, input.total);
  push(5, input.vat);

  // btoa is browser-side; Buffer covers the server and the test runner.
  const binary = String.fromCharCode(...chunks);
  if (typeof btoa === 'function') return btoa(binary);
  return Buffer.from(chunks).toString('base64');
}

/**
 * Render any text as an inline SVG QR code.
 *
 * Returns an empty string on failure — a receipt must print even if the QR
 * cannot be produced, and a missing square is better than a blank page.
 */
export function renderQrSvg(text: string, sizeMm = 28): string {
  if (!text) return '';
  try {
    const qr = QRCode.create(text, { errorCorrectionLevel: 'M' });
    const size = qr.modules.size;
    const data = qr.modules.data;

    // One path for every dark module beats one <rect> each: a 41×41 code is
    // ~700 rects, which noticeably slows the print preview.
    let path = '';
    for (let row = 0; row < size; row += 1) {
      for (let col = 0; col < size; col += 1) {
        if (data[row * size + col]) path += `M${col} ${row}h1v1h-1z`;
      }
    }

    return (
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" ` +
      `width="${sizeMm}mm" height="${sizeMm}mm" shape-rendering="crispEdges">` +
      `<rect width="${size}" height="${size}" fill="#fff"/>` +
      `<path d="${path}" fill="#000"/>` +
      `</svg>`
    );
  } catch {
    return '';
  }
}

/** Payload + SVG in one call, for the invoice template. */
export function buildZatcaQrSvg(input: {
  sellerName: string;
  vatNumber: string;
  timestamp: string;
  total: string;
  vat: string;
  sizeMm?: number;
}): string {
  const payload = buildZatcaPayload(input);
  return renderQrSvg(payload, input.sizeMm ?? 28);
}
